import { useEffect } from "react";
import type { NameTranslation } from "../../api/addressBook.service";
import { COMMON_LOCALES } from "./TranslationsList.constants";
import { areTranslationsValid, getTranslationRowError } from "./translationRowErrors";
import styles from "./TranslationsList.module.css";

const LOCALE_SUGGESTIONS_ID = "translations-locale-suggestions";

const ERROR_MESSAGES: Record<"invalid-locale" | "duplicate-locale", string> = {
  "invalid-locale": "Not a valid BCP-47 locale code",
  "duplicate-locale": "Duplicate locale",
};

type InheritedTranslation = NameTranslation & { source: string };

type TranslationsListProps = {
  translations: NameTranslation[];
  onChange: (translations: NameTranslation[]) => void;
  disabled?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  /**
   * Read-only rows inherited from a parent entity (e.g. an office showing its
   * firm's translations for locales it doesn't override). Purely display —
   * never included in onChange payloads or validity checks.
   */
  inheritedTranslations?: InheritedTranslation[];
};

export function TranslationsList({
  translations,
  onChange,
  disabled = false,
  onValidityChange,
  inheritedTranslations = [],
}: TranslationsListProps) {
  useEffect(() => {
    onValidityChange?.(areTranslationsValid(translations));
  }, [translations, onValidityChange]);

  function handleLocaleChange(index: number, value: string) {
    const next = translations.map((t, i) => (i === index ? { ...t, localeCode: value } : t));
    onChange(next);
  }

  function handleNameChange(index: number, value: string) {
    const next = translations.map((t, i) => (i === index ? { ...t, translatedName: value } : t));
    onChange(next);
  }

  function handleRemove(index: number) {
    onChange(translations.filter((_, i) => i !== index));
  }

  function handleAdd() {
    onChange([...translations, { localeCode: "", translatedName: "" }]);
  }

  const hasRows = translations.length > 0 || inheritedTranslations.length > 0;

  return (
    <div className={styles.translationsList}>
      {hasRows && (
        <div className={styles.translationsRows}>
          {inheritedTranslations.map(inherited => (
            <div
              key={`inherited-${inherited.localeCode}`}
              className={`${styles.translationRow} ${styles.translationRowInherited}`}
            >
              <div className={styles.translationField}>
                <span className={styles.translationLabel}>Locale code</span>
                <input
                  type="text"
                  className={styles.translationInput}
                  value={inherited.localeCode}
                  readOnly
                  disabled
                  aria-label={`Locale code (inherited from ${inherited.source})`}
                />
              </div>

              <div className={styles.translationField}>
                <span className={styles.translationLabel}>
                  Translated name
                  <span className={styles.inheritedTag}>Inherited</span>
                </span>
                <input
                  type="text"
                  className={styles.translationInput}
                  value={inherited.translatedName}
                  readOnly
                  disabled
                  aria-label={`Translated name (inherited from ${inherited.source})`}
                />
              </div>
            </div>
          ))}

          {translations.map((translation, index) => {
            const rowId = `translation-${index}`;
            const rowError = getTranslationRowError(translations, index);
            const localeError = rowError ? ERROR_MESSAGES[rowError] : null;
            return (
              <div key={index} className={styles.translationRow}>
                <div className={styles.translationField}>
                  <label className={styles.translationLabel} htmlFor={`${rowId}-locale`}>
                    Locale code
                  </label>
                  <input
                    id={`${rowId}-locale`}
                    type="text"
                    list={LOCALE_SUGGESTIONS_ID}
                    className={styles.translationInput}
                    value={translation.localeCode}
                    onChange={e => handleLocaleChange(index, e.target.value)}
                    placeholder="e.g. en-GB, zh-CN"
                    aria-invalid={localeError ? true : undefined}
                    disabled={disabled}
                  />
                  {localeError && <span className={styles.translationError}>{localeError}</span>}
                </div>

                <div className={styles.translationField}>
                  <label className={styles.translationLabel} htmlFor={`${rowId}-name`}>
                    Translated name
                  </label>
                  <input
                    id={`${rowId}-name`}
                    type="text"
                    className={styles.translationInput}
                    value={translation.translatedName}
                    onChange={e => handleNameChange(index, e.target.value)}
                    placeholder="Enter translated name"
                    disabled={disabled}
                  />
                </div>

                {!disabled && (
                  <button
                    type="button"
                    className={styles.translationRemoveButton}
                    onClick={() => handleRemove(index)}
                    aria-label={`Remove translation for ${translation.localeCode || "this locale"}`}
                  >
                    &times;
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <datalist id={LOCALE_SUGGESTIONS_ID}>
        {COMMON_LOCALES.map(locale => (
          <option key={locale.code} value={locale.code}>
            {locale.label}
          </option>
        ))}
      </datalist>

      {!disabled && (
        <button type="button" className={styles.addTranslationButton} onClick={handleAdd}>
          + Add translation
        </button>
      )}
    </div>
  );
}
